import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  Phone, 
  Calendar, 
  MapPin,
  Edit,
  Download,
  Plus,
  FileText,
  Paperclip,
  Trash,
  Pill,
  Save,
  X,
  Activity,
  ClipboardCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import SignatureCanvas from 'react-signature-canvas';

const PatientDetailPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { isDoctor } = useAuth();
  
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Visit form with vitals
  const [showVisitForm, setShowVisitForm] = useState(false);
  const [visitForm, setVisitForm] = useState({
    complaint: '',
    assessment: '',
    plan: '',
    follow_up_date: '',
    vitals: {
      blood_pressure: '',
      heart_rate: '',
      temperature: '',
      respiratory_rate: '',
      oxygen_saturation: '',
      weight: '',
      height: ''
    }
  });
  
  // RX form
  const [showRxForm, setShowRxForm] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState(null);
  const [medicines, setMedicines] = useState([{ name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
  const signatureRef = useRef(null);
  
  // Medical Certificate form
  const [showCertForm, setShowCertForm] = useState(false);
  const [certForm, setCertForm] = useState({
    template_type: 'general',
    diagnosis_summary: '',
    rest_days: '',
    date_issued: new Date().toISOString().split('T')[0]
  });
  const certSignatureRef = useRef(null);

  useEffect(() => {
    fetchPatient();
    fetchVisits();
    fetchPrescriptions();
    fetchCertificates();
  }, [patientId]);

  const fetchPatient = async () => {
    try {
      const response = await apiClient.get(`/patients/${patientId}`);
      setPatient(response.data);
      setEditForm(response.data);
    } catch (error) {
      toast.error('Patient not found');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchVisits = async () => {
    try {
      const response = await apiClient.get(`/visits?patient_id=${patientId}`);
      setVisits(response.data);
    } catch (error) {
      console.error('Failed to fetch visits');
    }
  };

  const fetchPrescriptions = async () => {
    try {
      const response = await apiClient.get(`/prescriptions?patient_id=${patientId}`);
      setPrescriptions(response.data);
    } catch (error) {
      console.error('Failed to fetch prescriptions');
    }
  };

  const fetchCertificates = async () => {
    try {
      const response = await apiClient.get(`/medical-certificates?patient_id=${patientId}`);
      setCertificates(response.data);
    } catch (error) {
      console.error('Failed to fetch certificates');
    }
  };

  const handleSavePatient = async () => {
    try {
      await apiClient.put(`/patients/${patientId}`, editForm);
      setPatient({ ...patient, ...editForm });
      setEditing(false);
      toast.success('Patient updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update patient');
    }
  };

  const handleDeletePatient = async () => {
    try {
      await apiClient.delete(`/patients/${patientId}`);
      toast.success('Patient deleted');
      navigate('/patients');
    } catch (error) {
      toast.error('Failed to delete patient');
    }
  };

  const handleCreateVisit = async () => {
    if (!visitForm.complaint) {
      toast.error('Please enter the complaint');
      return;
    }
    
    // Process vitals - only include non-empty values
    const vitals = {};
    Object.entries(visitForm.vitals).forEach(([key, value]) => {
      if (value !== '' && value !== null) {
        vitals[key] = ['heart_rate', 'respiratory_rate', 'oxygen_saturation'].includes(key) 
          ? parseInt(value) 
          : ['temperature', 'weight', 'height'].includes(key)
            ? parseFloat(value)
            : value;
      }
    });
    
    try {
      await apiClient.post('/visits', {
        patient_id: patientId,
        complaint: visitForm.complaint,
        assessment: visitForm.assessment,
        plan: visitForm.plan,
        follow_up_date: visitForm.follow_up_date || null,
        vitals: Object.keys(vitals).length > 0 ? vitals : null
      });
      toast.success('Visit recorded');
      setShowVisitForm(false);
      setVisitForm({ 
        complaint: '', 
        assessment: '', 
        plan: '', 
        follow_up_date: '',
        vitals: {
          blood_pressure: '',
          heart_rate: '',
          temperature: '',
          respiratory_rate: '',
          oxygen_saturation: '',
          weight: '',
          height: ''
        }
      });
      fetchVisits();
    } catch (error) {
      toast.error('Failed to create visit');
    }
  };

  const handleFileUpload = async (visitId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await apiClient.post(`/visits/${visitId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Attachment uploaded');
      fetchVisits();
    } catch (error) {
      toast.error('Failed to upload attachment');
    }
  };

  const handleCreateRx = async () => {
    if (!selectedVisitId) {
      toast.error('Please select a visit');
      return;
    }
    
    const validMedicines = medicines.filter(m => m.name && m.dosage);
    if (validMedicines.length === 0) {
      toast.error('Please add at least one medicine');
      return;
    }
    
    let signatureData = null;
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      signatureData = signatureRef.current.toDataURL('image/png').split(',')[1];
    }
    
    try {
      await apiClient.post('/prescriptions', {
        patient_id: patientId,
        visit_id: selectedVisitId,
        medicines: validMedicines,
        signature_data: signatureData
      });
      toast.success('Prescription created');
      setShowRxForm(false);
      setMedicines([{ name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
      setSelectedVisitId(null);
      fetchPrescriptions();
    } catch (error) {
      toast.error('Failed to create prescription');
    }
  };

  const handleCreateCertificate = async () => {
    if (!certForm.diagnosis_summary) {
      toast.error('Please enter the diagnosis summary');
      return;
    }
    
    let signatureData = null;
    if (certSignatureRef.current && !certSignatureRef.current.isEmpty()) {
      signatureData = certSignatureRef.current.toDataURL('image/png').split(',')[1];
    }
    
    try {
      await apiClient.post('/medical-certificates', {
        patient_id: patientId,
        template_type: certForm.template_type,
        diagnosis_summary: certForm.diagnosis_summary,
        rest_days: certForm.rest_days ? parseInt(certForm.rest_days) : null,
        date_issued: certForm.date_issued,
        signature_data: signatureData
      });
      toast.success('Medical certificate created');
      setShowCertForm(false);
      setCertForm({
        template_type: 'general',
        diagnosis_summary: '',
        rest_days: '',
        date_issued: new Date().toISOString().split('T')[0]
      });
      fetchCertificates();
    } catch (error) {
      toast.error('Failed to create certificate');
    }
  };

  const downloadRxPdf = async (prescriptionId) => {
    try {
      const response = await apiClient.get(`/prescriptions/${prescriptionId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prescription_${prescriptionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Prescription downloaded');
    } catch (error) {
      toast.error('Failed to download prescription');
    }
  };

  const downloadCertPdf = async (certificateId) => {
    try {
      const response = await apiClient.get(`/medical-certificates/${certificateId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `medical_cert_${certificateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Certificate downloaded');
    } catch (error) {
      toast.error('Failed to download certificate');
    }
  };

  const downloadPatientCard = async () => {
    try {
      const response = await apiClient.get(`/patients/${patientId}/card-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `patient_card_${patientId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Patient card downloaded');
    } catch (error) {
      toast.error('Failed to download patient card');
    }
  };

  const addMedicine = () => {
    setMedicines([...medicines, { name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
  };

  const updateMedicine = (index, field, value) => {
    const updated = [...medicines];
    updated[index][field] = value;
    setMedicines(updated);
  };

  const removeMedicine = (index) => {
    if (medicines.length > 1) {
      setMedicines(medicines.filter((_, i) => i !== index));
    }
  };

  const updateVitals = (field, value) => {
    setVisitForm({
      ...visitForm,
      vitals: { ...visitForm.vitals, [field]: value }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!patient) return null;

  const getTagBadge = (tag) => {
    const styles = {
      regular: 'bg-secondary text-secondary-foreground',
      pro_bono: 'bg-primary/10 text-primary',
      discount: 'bg-accent/10 text-accent',
    };
    const labels = {
      regular: 'Regular',
      pro_bono: 'Pro Bono',
      discount: 'Discount',
    };
    return (
      <Badge className={styles[tag] || styles.regular}>
        {labels[tag] || tag}
      </Badge>
    );
  };

  const certTemplateLabels = {
    fit_to_work: 'Fit to Work',
    sick_leave: 'Sick Leave',
    fit_to_travel: 'Fit to Travel',
    general: 'General Medical Certificate'
  };

  return (
    <div className="space-y-6" data-testid="patient-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/patients')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading text-2xl font-bold flex-1">Patient Record</h1>
        <Button variant="outline" onClick={downloadPatientCard} data-testid="download-card-btn">
          <Download className="w-4 h-4 mr-2" />
          Patient Card
        </Button>
      </div>

      {/* Patient Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {patient.photo_data ? (
                <img 
                  src={`data:image/png;base64,${patient.photo_data}`} 
                  alt={patient.full_name}
                  className="w-24 h-24 rounded-xl object-cover border-2 border-white shadow-md"
                />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-bold text-3xl">
                  {patient.full_name?.charAt(0) || '?'}
                </div>
              )}
            </div>

            {/* Info */}
            {editing ? (
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input 
                      value={editForm.full_name || ''}
                      onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Mobile</Label>
                    <Input 
                      value={editForm.mobile || ''}
                      onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Birthday</Label>
                    <Input 
                      type="date"
                      value={editForm.birthday || ''}
                      onChange={(e) => setEditForm({ ...editForm, birthday: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Sex</Label>
                    <Select value={editForm.sex || ''} onValueChange={(v) => setEditForm({ ...editForm, sex: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Male</SelectItem>
                        <SelectItem value="F">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Address</Label>
                    <Input 
                      value={editForm.address || ''}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Tag</Label>
                    <Select value={editForm.tag || 'regular'} onValueChange={(v) => setEditForm({ ...editForm, tag: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="regular">Regular</SelectItem>
                        <SelectItem value="pro_bono">Pro Bono</SelectItem>
                        <SelectItem value="discount">Doctor's Discount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSavePatient} data-testid="save-patient-btn">
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="font-heading text-xl font-bold">{patient.full_name}</h2>
                  {getTagBadge(patient.tag)}
                </div>
                <p className="text-sm text-muted-foreground mb-4">ID: {patient.patient_id}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    {patient.mobile}
                  </div>
                  {patient.birthday && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      {patient.birthday}
                    </div>
                  )}
                  {patient.address && (
                    <div className="flex items-center gap-2 col-span-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      {patient.address}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-patient-btn">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button variant="outline" onClick={() => setShowCertForm(true)} data-testid="create-cert-btn">
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                    Medical Certificate
                  </Button>
                  {isDoctor && (
                    <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                      <Trash className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="visits">
        <TabsList>
          <TabsTrigger value="visits">Visits ({visits.length})</TabsTrigger>
          <TabsTrigger value="prescriptions">Prescriptions ({prescriptions.length})</TabsTrigger>
          <TabsTrigger value="certificates">Certificates ({certificates.length})</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* Visits Tab */}
        <TabsContent value="visits" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium">Visit History</h3>
            <Button onClick={() => setShowVisitForm(true)} data-testid="add-visit-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Visit
            </Button>
          </div>

          {visits.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No visits recorded</p>
              </CardContent>
            </Card>
          ) : (
            visits.map((visit, index) => (
              <Card key={visit.visit_id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {new Date(visit.created_at).toLocaleDateString('en-PH', { 
                          year: 'numeric', month: 'long', day: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                      {visit.follow_up_date && (
                        <Badge variant={visit.follow_up_status === 'missed' ? 'destructive' : 'outline'} className="mt-1">
                          Follow-up: {visit.follow_up_date}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedVisitId(visit.visit_id);
                          setShowRxForm(true);
                        }}
                        data-testid={`create-rx-${visit.visit_id}`}
                      >
                        <Pill className="w-4 h-4 mr-1" />
                        Create RX
                      </Button>
                      <label className="cursor-pointer">
                        <Input 
                          type="file" 
                          className="hidden" 
                          onChange={(e) => handleFileUpload(visit.visit_id, e)}
                          accept="image/*,.pdf"
                        />
                        <Button size="sm" variant="ghost" asChild>
                          <span><Paperclip className="w-4 h-4" /></span>
                        </Button>
                      </label>
                    </div>
                  </div>

                  {/* Vitals Display */}
                  {visit.vitals && Object.keys(visit.vitals).some(k => visit.vitals[k]) && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">Vitals</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        {visit.vitals.blood_pressure && (
                          <div><span className="text-muted-foreground">BP:</span> {visit.vitals.blood_pressure}</div>
                        )}
                        {visit.vitals.heart_rate && (
                          <div><span className="text-muted-foreground">HR:</span> {visit.vitals.heart_rate} bpm</div>
                        )}
                        {visit.vitals.temperature && (
                          <div><span className="text-muted-foreground">Temp:</span> {visit.vitals.temperature}°C</div>
                        )}
                        {visit.vitals.respiratory_rate && (
                          <div><span className="text-muted-foreground">RR:</span> {visit.vitals.respiratory_rate}/min</div>
                        )}
                        {visit.vitals.oxygen_saturation && (
                          <div><span className="text-muted-foreground">SpO2:</span> {visit.vitals.oxygen_saturation}%</div>
                        )}
                        {visit.vitals.weight && (
                          <div><span className="text-muted-foreground">Weight:</span> {visit.vitals.weight} kg</div>
                        )}
                        {visit.vitals.height && (
                          <div><span className="text-muted-foreground">Height:</span> {visit.vitals.height} cm</div>
                        )}
                        {visit.vitals.bmi && (
                          <div><span className="text-muted-foreground">BMI:</span> {visit.vitals.bmi}</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Complaint</p>
                      <p className="text-sm">{visit.complaint}</p>
                    </div>
                    {visit.assessment && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase">Assessment</p>
                        <p className="text-sm">{visit.assessment}</p>
                      </div>
                    )}
                    {visit.plan && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase">Plan</p>
                        <p className="text-sm">{visit.plan}</p>
                      </div>
                    )}
                  </div>

                  {visit.attachments?.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Attachments</p>
                      <div className="flex flex-wrap gap-2">
                        {visit.attachments.map((att) => (
                          <Button
                            key={att.attachment_id}
                            size="sm"
                            variant="secondary"
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/visits/${visit.visit_id}/attachments/${att.attachment_id}`, '_blank')}
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            {att.filename}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Prescriptions Tab */}
        <TabsContent value="prescriptions" className="space-y-4">
          <h3 className="font-medium">Prescriptions</h3>
          
          {prescriptions.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No prescriptions yet</p>
              </CardContent>
            </Card>
          ) : (
            prescriptions.map((rx, index) => (
              <Card key={rx.prescription_id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {new Date(rx.created_at).toLocaleDateString('en-PH', { 
                          year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">Prescribed by: {rx.doctor_name}</p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => downloadRxPdf(rx.prescription_id)}
                      data-testid={`download-rx-${rx.prescription_id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {rx.medicines.map((med, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 rounded bg-muted/50">
                        <Pill className="w-4 h-4 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm font-medium">{med.name} - {med.dosage}</p>
                          <p className="text-xs text-muted-foreground">
                            {med.frequency} for {med.duration}
                            {med.instructions && ` • ${med.instructions}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium">Medical Certificates</h3>
            <Button onClick={() => setShowCertForm(true)} data-testid="add-cert-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Certificate
            </Button>
          </div>
          
          {certificates.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No certificates yet</p>
              </CardContent>
            </Card>
          ) : (
            certificates.map((cert, index) => (
              <Card key={cert.certificate_id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <ClipboardCheck className="w-4 h-4 text-primary" />
                        <span className="font-medium">{certTemplateLabels[cert.template_type]}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">Issued: {cert.date_issued}</p>
                      <p className="text-sm mt-2 line-clamp-2">{cert.diagnosis_summary}</p>
                      {cert.rest_days && (
                        <Badge variant="outline" className="mt-2">
                          Rest: {cert.rest_days} day(s)
                        </Badge>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => downloadCertPdf(cert.certificate_id)}
                      data-testid={`download-cert-${cert.certificate_id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div>
                <Label>Staff Notes (visible to all staff)</Label>
                <Textarea
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Add notes about this patient..."
                  rows={4}
                />
              </div>
              {isDoctor && (
                <div>
                  <Label>Private Notes (doctor only)</Label>
                  <Textarea
                    value={editForm.private_notes || ''}
                    onChange={(e) => setEditForm({ ...editForm, private_notes: e.target.value })}
                    placeholder="Private notes visible only to doctors..."
                    rows={4}
                  />
                </div>
              )}
              <Button onClick={handleSavePatient}>Save Notes</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Visit Form Dialog with Vitals */}
      <Dialog open={showVisitForm} onOpenChange={setShowVisitForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record New Visit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Vitals Section */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Vitals (Optional)</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Blood Pressure</Label>
                  <Input
                    value={visitForm.vitals.blood_pressure}
                    onChange={(e) => updateVitals('blood_pressure', e.target.value)}
                    placeholder="120/80"
                  />
                </div>
                <div>
                  <Label className="text-xs">Heart Rate (bpm)</Label>
                  <Input
                    type="number"
                    value={visitForm.vitals.heart_rate}
                    onChange={(e) => updateVitals('heart_rate', e.target.value)}
                    placeholder="72"
                  />
                </div>
                <div>
                  <Label className="text-xs">Temperature (°C)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={visitForm.vitals.temperature}
                    onChange={(e) => updateVitals('temperature', e.target.value)}
                    placeholder="36.5"
                  />
                </div>
                <div>
                  <Label className="text-xs">Respiratory Rate</Label>
                  <Input
                    type="number"
                    value={visitForm.vitals.respiratory_rate}
                    onChange={(e) => updateVitals('respiratory_rate', e.target.value)}
                    placeholder="16"
                  />
                </div>
                <div>
                  <Label className="text-xs">SpO2 (%)</Label>
                  <Input
                    type="number"
                    value={visitForm.vitals.oxygen_saturation}
                    onChange={(e) => updateVitals('oxygen_saturation', e.target.value)}
                    placeholder="98"
                  />
                </div>
                <div>
                  <Label className="text-xs">Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={visitForm.vitals.weight}
                    onChange={(e) => updateVitals('weight', e.target.value)}
                    placeholder="65"
                  />
                </div>
                <div>
                  <Label className="text-xs">Height (cm)</Label>
                  <Input
                    type="number"
                    value={visitForm.vitals.height}
                    onChange={(e) => updateVitals('height', e.target.value)}
                    placeholder="165"
                  />
                </div>
                {visitForm.vitals.weight && visitForm.vitals.height && (
                  <div className="flex items-end">
                    <div className="p-2 bg-white rounded border w-full text-center">
                      <span className="text-xs text-muted-foreground">BMI: </span>
                      <span className="font-medium">
                        {(parseFloat(visitForm.vitals.weight) / Math.pow(parseFloat(visitForm.vitals.height) / 100, 2)).toFixed(1)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Complaint *</Label>
              <Textarea
                value={visitForm.complaint}
                onChange={(e) => setVisitForm({ ...visitForm, complaint: e.target.value })}
                placeholder="What is the patient's complaint?"
                rows={2}
              />
            </div>
            <div>
              <Label>Assessment</Label>
              <Textarea
                value={visitForm.assessment}
                onChange={(e) => setVisitForm({ ...visitForm, assessment: e.target.value })}
                placeholder="Your assessment..."
                rows={2}
              />
            </div>
            <div>
              <Label>Plan</Label>
              <Textarea
                value={visitForm.plan}
                onChange={(e) => setVisitForm({ ...visitForm, plan: e.target.value })}
                placeholder="Treatment plan..."
                rows={2}
              />
            </div>
            <div>
              <Label>Follow-up Date</Label>
              <Input
                type="date"
                value={visitForm.follow_up_date}
                onChange={(e) => setVisitForm({ ...visitForm, follow_up_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowVisitForm(false)}>Cancel</Button>
            <Button onClick={handleCreateVisit} data-testid="save-visit-btn">Save Visit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RX Form Dialog */}
      <Dialog open={showRxForm} onOpenChange={setShowRxForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Prescription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Medicines */}
            <div>
              <Label>Medicines</Label>
              {medicines.map((med, index) => (
                <div key={index} className="border rounded-lg p-3 mt-2 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Medicine {index + 1}</span>
                    {medicines.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => removeMedicine(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Medicine name"
                      value={med.name}
                      onChange={(e) => updateMedicine(index, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Dosage (e.g., 500mg)"
                      value={med.dosage}
                      onChange={(e) => updateMedicine(index, 'dosage', e.target.value)}
                    />
                    <Input
                      placeholder="Frequency (e.g., 3x daily)"
                      value={med.frequency}
                      onChange={(e) => updateMedicine(index, 'frequency', e.target.value)}
                    />
                    <Input
                      placeholder="Duration (e.g., 7 days)"
                      value={med.duration}
                      onChange={(e) => updateMedicine(index, 'duration', e.target.value)}
                    />
                  </div>
                  <Input
                    placeholder="Instructions (optional)"
                    value={med.instructions}
                    onChange={(e) => updateMedicine(index, 'instructions', e.target.value)}
                  />
                </div>
              ))}
              <Button variant="outline" className="mt-2 w-full" onClick={addMedicine}>
                <Plus className="w-4 h-4 mr-2" />
                Add Another Medicine
              </Button>
            </div>

            {/* Signature */}
            <div>
              <Label>Doctor's Signature</Label>
              <div className="border rounded-lg p-2 bg-white mt-1">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-32 border rounded',
                    style: { width: '100%', height: '128px' }
                  }}
                />
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-1"
                onClick={() => signatureRef.current?.clear()}
              >
                Clear Signature
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRxForm(false)}>Cancel</Button>
            <Button onClick={handleCreateRx} data-testid="save-rx-btn">Create Prescription</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medical Certificate Dialog */}
      <Dialog open={showCertForm} onOpenChange={setShowCertForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Medical Certificate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Certificate Type</Label>
              <Select value={certForm.template_type} onValueChange={(v) => setCertForm({ ...certForm, template_type: v })}>
                <SelectTrigger data-testid="cert-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fit_to_work">Fit to Work</SelectItem>
                  <SelectItem value="sick_leave">Sick Leave</SelectItem>
                  <SelectItem value="fit_to_travel">Fit to Travel</SelectItem>
                  <SelectItem value="general">General Medical Certificate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Diagnosis / Summary *</Label>
              <Textarea
                value={certForm.diagnosis_summary}
                onChange={(e) => setCertForm({ ...certForm, diagnosis_summary: e.target.value })}
                placeholder="Brief diagnosis or medical summary..."
                rows={3}
              />
            </div>
            {certForm.template_type === 'sick_leave' && (
              <div>
                <Label>Recommended Rest Days</Label>
                <Input
                  type="number"
                  value={certForm.rest_days}
                  onChange={(e) => setCertForm({ ...certForm, rest_days: e.target.value })}
                  placeholder="Number of days"
                />
              </div>
            )}
            <div>
              <Label>Date Issued</Label>
              <Input
                type="date"
                value={certForm.date_issued}
                onChange={(e) => setCertForm({ ...certForm, date_issued: e.target.value })}
              />
            </div>
            <div>
              <Label>Doctor's Signature</Label>
              <div className="border rounded-lg p-2 bg-white mt-1">
                <SignatureCanvas
                  ref={certSignatureRef}
                  canvasProps={{
                    className: 'w-full h-32 border rounded',
                    style: { width: '100%', height: '128px' }
                  }}
                />
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-1"
                onClick={() => certSignatureRef.current?.clear()}
              >
                Clear Signature
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCertForm(false)}>Cancel</Button>
            <Button onClick={handleCreateCertificate} data-testid="save-cert-btn">Create Certificate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Patient?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {patient.full_name}'s record and all associated visits, prescriptions, and attachments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePatient} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PatientDetailPage;
